import axios from 'axios';
import type { AdminSession } from '../store/auth-store';

// We hit the same baseURL as the main API client but build a dedicated axios
// instance so auth requests aren't intercepted by the auth/refresh chain
// (which would loop forever on /auth/admin/refresh failures).
// const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const authClient = axios.create({ baseURL, withCredentials: true, timeout: 15_000 });

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
  actorType: 'ADMIN';
  role: string;
  scope: string[];
}

export interface LoginResult {
  twoFactorEnabled: boolean;
  challengeToken?: string;
  expiresInSec: number;
  /**
   * No second factor yet: `challengeToken` is an enrolment token, used as the
   * bearer for startTotpSetup / enableTotp. Login never returns a session.
   */
  enrolmentRequired?: boolean;
  /** @deprecated legacy bootstrap path — the API no longer returns it. */
  tokens?: AuthTokens;
}

export interface TotpSetup {
  secret: string;
  otpauthUrl: string;
  /** A factor is already on — enableTotp then needs `currentTotp` as well. */
  reenrolment: boolean;
}

function toSession(tokens: AuthTokens): AdminSession {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresInSec: tokens.expiresInSec,
    actorType: tokens.actorType,
    role: tokens.role,
    scope: tokens.scope,
    expiresAt: new Date(Date.now() + tokens.expiresInSec * 1000).toISOString(),
  };
}

export const authService = {
  async login(email: string, password: string): Promise<LoginResult> {
    const { data } = await authClient.post<LoginResult>('/auth/admin/login', { email, password });
    return data;
  },

  async verify2fa(challengeToken: string, totp: string): Promise<AdminSession> {
    const { data } = await authClient.post<AuthTokens>('/auth/admin/2fa', {
      challengeToken,
      totp,
    });
    return toSession(data);
  },

  async refresh(refreshToken: string): Promise<AdminSession> {
    const { data } = await authClient.post<AuthTokens>('/auth/admin/refresh', { refreshToken });
    return toSession(data);
  },

  async logout(accessToken: string): Promise<void> {
    await authClient.post(
      '/auth/admin/logout',
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
  },

  /** [accessToken] is a session token, or the enrolment token from login. */
  async startTotpSetup(accessToken: string): Promise<TotpSetup> {
    const { data } = await authClient.post<TotpSetup>(
      '/auth/admin/2fa/setup',
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return data;
  },

  /**
   * Returns the admin's first session on first enrolment (enrolment-token
   * bearer, API answers 200 + tokens), or null on re-enrolment (204).
   */
  async enableTotp(accessToken: string, totp: string, currentTotp?: string): Promise<AdminSession | null> {
    const res = await authClient.post<AuthTokens | ''>(
      '/auth/admin/2fa/enable',
      currentTotp ? { totp, currentTotp } : { totp },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return res.status === 200 && res.data ? toSession(res.data) : null;
  },

  toSession,
};
